<template>
  <b-col v-if="loading">
    <b-skeleton
      animation="wave"
      class="mx-auto"
      width="60%"
      height="400px"
    />
  </b-col>
  <section v-else>
    <scroll-container
      class="pt-2 mt-n30px"
      height-offset="140px"
    >
      <b-container
        class="container-small"
      >
        <b-card
          class="mb-2"
        >
          <b-form-group
            class="mb-2"
            :label="$tk('ClientPortal.Property.Targeting.Name') + ' *'"
            label-for="input-targting-name"
            :invalid-feedback="errorMessages.Name"
          >
            <b-form-input
              id="input-targting-name"
              v-model="wrapper.targetingProperty.name"
              :placeholder="$tk('ClientPortal.Property.Targeting.NamePlaceholder')"
              :state="errorMessages.Name == null ? null : false"
            />
          </b-form-group>
          <b-form-group
            class="mb-2"
            :label="$tk('ClientPortal.Property.Targeting.Value') + ' *'"
            label-for="input-targeting-value"
            :invalid-feedback="errorMessages.Value"
          >
            <b-form-input
              id="input-targeting-value"
              v-model="wrapper.targetingProperty.value"
              :placeholder="$tk('ClientPortal.Property.Targeting.ValuePlaceholder')"
              :state="errorMessages.Value == null ? null : false"
            />
          </b-form-group>
          <b-form-group
            class="mb-2"
            :label="$tk('ClientPortal.Property.Targeting.TargetingPropertyType') + ' *'"
            label-for="key-type-select"
            :invalid-feedback="errorMessages.Type"
            :state="errorMessages.Type == null ? null : false"
          >
            <v-select
              v-model="wrapper.targetingProperty.type"
              name="key-type-select"
              :options="wrapper.targetingPropertyTypes"
              label="text"
              :disabled="wrapper.targetingProperty.id !== 0"
              taggable
              :clearable="false"
              select-on-tab
              :reduce="f => f.value"
            />
          </b-form-group>
          <b-form-group
            class="mb-2"
            :label="$tk('ClientPortal.Property.Targeting.MatchType') + ' *'"
            label-for="match-type-select"
            :invalid-feedback="errorMessages.MatchType"
            :state="errorMessages.MatchType == null ? null : false"
          >
            <v-select
              v-model="wrapper.targetingProperty.matchType"
              name="match-type-select"
              :options="wrapper.targetingPropertyMatchTypes"
              label="text"
              taggable
              :clearable="false"
              select-on-tab
              :reduce="f => f.value"
            />
          </b-form-group>
          <b-form-group
            :class="wrapper.targetingProperty.privacyType == 2 ? 'mb-2' : 'mb-0'"
            :label="$tk('ClientPortal.Property.Targeting.PrivacyType') + ' *'"
            label-for="targeting-privacy-type-select"
          >
            <v-select
              :value="wrapper.targetingProperty.privacyType"
              name="targeting-privacy-type-select"
              :options="wrapper.targetingPropertyPrivacyTypes"
              label="text"
              taggable
              :clearable="false"
              select-on-tab
              :reduce="f => f.value"
              @input="(value, name) => updateTargetingPropertyPrivacyStatus(value)"
            />
          </b-form-group>
          <AccountsInput
            v-if="wrapper.targetingProperty.privacyType === 2"
            id="TargetingSharedAccounts"
            :accounts="availableAccounts"
            :error-message="errorMessages['TargetingSharedAccounts']"
            :has-selected-accounts="wrapper.targetingProperty.targetingSharedAccounts.length > 0"
            @search="searchAccounts"
            @add="addTargetingPrivacyAccount"
          />
          <SelectedItems
            v-if="wrapper.targetingProperty.privacyType == 2 && wrapper.targetingProperty.targetingSharedAccounts.length"
            :items="wrapper.targetingProperty.targetingSharedAccounts"
            id-label="accountId"
            variant="avatar"
            @remove="removeSelectedSharedAccount"
          />
        </b-card>
        <template v-if="isTargetingPropertyPredefined(wrapper.targetingProperty.type) && wrapper.targetingProperty.id !== 0">
          <b-button
            variant="primary"
            class="mb-2"
            size="sm"
            @click="showTargetingOptionModal()"
          >
            <GjIcon
              class="mr-50"
              name="Plus"
            />
            {{ $tk('ClientPortal.Property.Targeting.CreateTargetingOption') }}
          </b-button>
          <b-card
            no-body
          >
            <b-table
              :items="wrapper.targetingOptions"
              :fields="fields"
              show-empty
              class="client-table__data table-content"
              :empty-text="$tk('ClientPortal.Targeting.NoTargetingOptionsHaveBeenCreatedYet')"
              :current-page="currentPage"
              :per-page="perPage"
              responsive
            >
              <template #empty="scope">
                <div class="text-center p-3">
                  <GjIcon
                    size="64px"
                    name="Widget"
                  />
                  <div class="mb-50">
                    {{ scope.emptyText }}
                  </div>
                  <b-link @click="showTargetingOptionModal()">
                    <GjIcon
                      size="14"
                      name="Plus"
                    />
                    <span class="create-text">{{ $tk('ClientPortal.Property.Targeting.CreateTargetingOption') }}</span>
                  </b-link>
                </div>
              </template>
              <template #cell(value)="data">
                <div class="d-flex align-items-center justify-content-between">
                  <span>
                    {{ data.item.value }}
                  </span>
                  <b-dropdown
                    variant="link"
                    toggle-class="text-decoration-none"
                    no-caret
                    button-class="p-0"
                    right
                  >
                    <template v-slot:button-content>
                      <GjIcon name="Moreoptions_fill" />
                    </template>
                    <b-dropdown-item @click="editTargetingOption(data.item)">
                      <GjIcon
                        class="mr-50"
                        name="Edit"
                      />
                      {{ $tk('ClientPortal.Property.Targeting.EditTargetingOption') }}
                    </b-dropdown-item>
                    <b-dropdown-item @click="showConfirmTargetingOptionDeleteModal(data.item, data.index)">
                      <GjIcon
                        class="mr-50"
                        name="Delete"
                      />
                      {{ $tk('ClientPortal.Property.Targeting.DeleteTargetingOption') }}
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </template>
            </b-table>
            <b-card-footer
              v-if="wrapper.targetingOptions.length > 0"
              class="client-table__footer d-flex"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                size="sm"
                class="my-0 mr-1"
                hide-goto-end-buttons
              >
                <template #prev-text>
                  <GjIcon
                    class="mr-25"
                    name="ArrowLeft"
                  />
                </template>
                <template #next-text>
                  <GjIcon
                    class="ml-25"
                    name="ArrowRight"
                  />
                </template>
              </b-pagination>
              <span>
                {{ $tk('ClientPortal.Pagination.Viewing') }} {{ fromCount }} {{ $tk('ClientPortal.Pagination.To') }} {{ toCount }} {{ $tk('ClientPortal.Pagination.Of') }} {{ totalRows }} {{ $tk('ClientPortal.Pagination.Items') }}
              </span>
            </b-card-footer>
          </b-card>
        </template>
      </b-container>
    </scroll-container>
    <div class="border-top text-right py-1">
      <div class="container-small mx-auto">
        <b-button
          :disabled="saving"
          class="bg-white"
          size="sm"
          variant="outline-dark"
          :to="`/${$route.params.accountId}/property/details?propertyId=${propertyId}#targeting`"
        >
          {{ $tk('Form.Actions.Cancel') }}
        </b-button>
        <b-button
          class="bg-white ml-50"
          variant="primary"
          size="sm"
          :disabled="saving || !isFormComplete"
          @click.prevent="saveTargetingProperty(wrapper.targetingProperty)"
        >
          {{ $tk('ClientPortal.Form.Actions.Save') }}
        </b-button>
      </div>
    </div>
    <b-modal
      id="targeting-option-modal"
      ref="targeting-option-modal"
      centered
      :title="optionModel.id !== 0 ? $tk('ClientPortal.Form.EditTargetingOption') : $tk('ClientPortal.Form.CreateTargetingOption')"
    >
      <b-form-group
        :label="$tk('ClientPortal.Property.Targeting.Name') + ' *'"
        :invalid-feedback="optionErrorMessages.Name"
      >
        <b-form-input
          v-model="optionModel.name"
          type="text"
          :state="optionErrorMessages.Name == null ? null : false"
          :placeholder="$tk('ClientPortal.Property.Targeting.NamePlaceholder')"
        />
      </b-form-group>
      <b-form-group
        :label="$tk('ClientPortal.Property.Targeting.Value') + ' *'"
        :invalid-feedback="optionErrorMessages.Value"
      >
        <b-form-input
          v-model="optionModel.value"
          type="text"
          class="mb-0"
          :state="optionErrorMessages.Value == null ? null : false"
          :placeholder="$tk('ClientPortal.Property.Targeting.ValuePlaceholder')"
        />
      </b-form-group>
      <template #modal-footer="{ cancel, close }">
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="optionSaving"
          @click.prevent="cancel()"
        >
          {{ $tk('Form.Actions.Cancel') }}
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="optionSaving"
          @click.prevent="saveTargetingOption(optionModel),close()"
        >
          {{ $tk('Form.Actions.Save') }}
        </b-button>
      </template>
    </b-modal>
    <b-modal
      id="confirm-targeting-option-delete-modal"
      ref="confirm-targeting-option-delete-modal"
      centered
      :title="$tk('ClientPortal.Property.Targeting.AreYouSureYouWantToDeleteThisTargetingOption')"
    >
      <span>{{ $tk('ClientPortal.Property.Targeting.OptionName') }}: {{ confirmModal.name }}</span>
      <template #modal-footer="{ cancel, close }">
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="optionSaving"
          @click.prevent="cancel()"
        >
          {{ $tk('Form.Actions.Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          :disabled="optionSaving"
          @click.prevent="deleteTargetingOption(confirmModal.optionId, confirmModal.index),close()"
        >
          {{ $tk('Form.Actions.Delete') }}
        </b-button>
      </template>
    </b-modal>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import ScrollContainer from '@/components/ScrollContainer.vue'
import notification from '@/mixins/toastification'
import TargetingService from '@/api/client/targeting.service'
import store from '@/store'
import vSelect from 'vue-select'
import AccountsService from '@/api/client/accounts.service'
import SelectedItems from '@/components/SelectedItems.vue'
import AccountsInput from '@/components/AccountsInput.vue'

const targetingPropertyType = {
  PREDEFINED: 1,
  FREE_FORM: 2,
}

const propertyTargetingStatus = {
  ACTIVE: 1,
  DELETED: 2,
}

const PRIVACY_STATUS = Object.freeze({
  PRIVATE: 1,
  SHARED: 2,
  PUBLIC: 3,
})

export default {
  components: {
    GjIcon,
    ScrollContainer,
    vSelect,
    SelectedItems,
    AccountsInput,
  },
  mixins: [notification],
  data() {
    return {
      wrapper: {},
      optionModel: {},
      errorMessages: {},
      optionErrorMessages: {},
      availableAccounts: [],
      confirmModal: {},
      saving: false,
      optionSaving: false,
      loading: true,
      propertyId: 0,
      targetingPropertyId: 0,
      currentPage: 1,
      perPage: 10,
    }
  },
  computed: {
    fromCount() {
      return (this.currentPage - 1) * this.perPage + 1
    },
    toCount() {
      const toCount = this.fromCount + this.perPage - 1
      return Math.min(toCount, this.totalRows)
    },
    isFormComplete() {
      const {
        name, value, type, matchType,
      } = this.wrapper.targetingProperty

      return name && value && type && matchType
    },
    totalRows() {
      return this.wrapper.targetingOptions.length
    },
    fields() {
      return [
        {
          key: 'name',
          label: this.$tk('ClientPortal.Property.Targeting.Name'),
        },
        {
          key: 'value',
          label: this.$tk('ClientPortal.Property.Targeting.Value'),
        },
      ]
    },
  },
  async mounted() {
    this.propertyId = this.$route.query.propertyId !== undefined ? this.$route.query.propertyId : 0
    this.targetingPropertyId = this.$route.query.targetingPropertyId !== undefined ? this.$route.query.targetingPropertyId : 0
    try {
      this.wrapper = await TargetingService.getCustomTargetingProperty(this.propertyId, this.targetingPropertyId, this.$route.params.accountId)
      if (this.wrapper.targetingProperty.privacyType === PRIVACY_STATUS.SHARED) { this.searchAccounts() }
    } catch (error) {
      this.error(this.$tk('ClientPortal.Form.FailedToLoadData'))
    } finally {
      this.loading = false
    }
  },
  methods: {
    showTargetingOptionModal() {
      this.optionErrorMessages = {}
      this.optionModel = {
        id: 0,
        propertyKeyId: this.wrapper.targetingProperty.id,
        name: '',
        value: '',
        status: propertyTargetingStatus.ACTIVE,
      }
      this.$refs['targeting-option-modal'].show()
    },
    showConfirmTargetingOptionDeleteModal(option, index) {
      this.confirmModal = { name: option.name, optionId: option.id, index }
      this.$refs['confirm-targeting-option-delete-modal'].show()
    },
    hideConfirmTargetingOptionDeleteModal() {
      this.$refs['confirm-targeting-option-delete-modal'].hide()
    },
    hideTargetingOptionModal() {
      this.$refs['targeting-option-modal'].hide()
    },
    editTargetingOption(value) {
      this.optionModel = JSON.parse(JSON.stringify(value))
      this.optionErrorMessages = {}
      this.$refs['targeting-option-modal'].show()
    },
    async saveTargetingProperty(obj) {
      this.saving = true
      try {
        const response = await TargetingService.createCustomTargetingProperty(this.propertyId, this.wrapper.targetingProperty, this.$route.params.accountId)
        if (response.saved) {
          if (obj.id === 0) {
            store.commit('app/TOGGLE_NEW_CREATE_STATE', true)
          }
          this.success(response.message)
          this.$router.push(`/${this.$route.params.accountId}/property/details?propertyId=${this.wrapper.targetingProperty.propertyId}#targeting`)
        } else {
          this.errorMessages = response.errors
          this.error(response.message)
        }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.saving = false
      }
    },
    async saveTargetingOption() {
      this.optionSaving = true
      try {
        const response = await TargetingService.createCustomTargetingOption(this.propertyId, this.targetingPropertyId, this.optionModel, this.$route.params.accountId)
        if (response.saved) {
          if (this.optionModel.id === 0) {
            this.optionModel.id = response.id
            this.wrapper.targetingOptions.push(this.optionModel)
          } else {
            const targetingOption = this.wrapper.targetingOptions.find(x => x.id === this.optionModel.id)
            targetingOption.name = this.optionModel.name
            targetingOption.value = this.optionModel.value
          }
          this.$refs['targeting-option-modal'].hide()
          this.success(response.message)
        } else {
          this.optionErrorMessages = response.errors
          this.error(response.message)
        }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.optionSaving = false
      }
    },
    async deleteTargetingOption(optionId, index) {
      this.optionSaving = true
      try {
        const response = await TargetingService.deleteCustomTargetingOption(this.propertyId, this.targetingPropertyId, optionId, this.$route.params.accountId)
        if (response.saved) {
          this.wrapper.targetingOptions.splice(index, 1)
          this.success(response.message)
        } else { this.error(response.message) }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.optionSaving = false
        this.hideConfirmTargetingOptionDeleteModal()
      }
    },
    isTargetingPropertyPredefined(type) {
      return type === targetingPropertyType.PREDEFINED
    },
    async updateTargetingPropertyPrivacyStatus(value) {
      const { privacyType } = this.wrapper.targetingProperty
      if (privacyType === value) { return }
      this.wrapper.targetingProperty.privacyType = value
      this.wrapper.targetingProperty.targetingSharedAccounts = []
      if (value === PRIVACY_STATUS.SHARED) { this.searchAccounts() }
    },
    async searchAccounts(term = '') {
      try {
        const accounts = await AccountsService.searchAccounts(term, this.$route.params.accountId)
        this.availableAccounts = accounts.filter(x => x.id !== this.$route.params.accountId)
      } catch (err) {
        this.error(err.message)
      }
    },
    addTargetingPrivacyAccount(selectedItem) {
      if (this.wrapper.targetingProperty.targetingSharedAccounts.some(x => x.accountId === selectedItem.id)) { return }
      const account = {
        id: 0,
        accountId: selectedItem.id,
        name: selectedItem.name,
        imageUrl: selectedItem.imageUrl,
      }
      this.wrapper.targetingProperty.targetingSharedAccounts.push(account)
    },
    removeSelectedSharedAccount(index) {
      this.wrapper.targetingProperty.targetingSharedAccounts.splice(index, 1)
    },
  },
}
</script>
<style scoped lang="scss">
  .table-content {
    min-height: 35vh;
  }
</style>
